/**
 * Permission catalogue (D-034, D-129 as implemented in C17, D-209, D-214,
 * D-215). Permissions are data: a role holds a set of tokens, a user holds a
 * role plus per-user grants / revocations, and the Super Manager role holds
 * every token by definition (D-129). The pseudo-token `all` and name-based
 * privileges of the old app (`admin`, `super`, …; D08 R-U1, R-U8) do not exist.
 *
 * Sources: the 31 tokens of `original/src/lib/permissions.ts:3-35` with their
 * labels (`:78-141`), kept per D-129; the extensions D-129 lists; the engine's
 * tokens (B1_STATUS §3); `recovery_approve` (D-209); `create_customers`
 * (D-018); `approve_operations` (D-035).
 */

export type PermissionGroup =
  | "sections" // opens a sidebar section (tab guard uses tokens, D-129)
  | "dashboard_cards"
  | "movements"
  | "customers"
  | "money"
  | "custody"
  | "settings" // per-card settings tokens (D-129 "manage settings (per card)")
  | "users"
  | "audit";

export interface PermissionDef {
  readonly token: string;
  readonly group: PermissionGroup;
  readonly ar: string;
  readonly en: string;
  /** `old` = one of the 31 tokens of the old app (label kept); otherwise the decision that adds it. */
  readonly origin: "old" | string;
}

const P = (token: string, group: PermissionGroup, ar: string, en: string, origin: PermissionDef["origin"] = "old"): PermissionDef => ({
  token,
  group,
  ar,
  en,
  origin,
});

/** The catalogue, in display order. */
export const PERMISSIONS: readonly PermissionDef[] = [
  // --- the 31 tokens of the old app (P:3-35), labels from P:78-141 ---
  P("dashboard", "sections", "لوحة التحكم", "Dashboard"),
  P("movements", "sections", "سجل الحركات اليومية", "Daily Movements Log"),
  P("debts", "sections", "إدارة العملاء", "Customer Management"),
  P("wallets", "sections", "الصناديق والخزنة", "Boxes & Vault"),
  P("currencies", "sections", "عرض خزنة العملات", "View Currency Vault"),
  P("dollar", "sections", "التعامل بالدولار", "Dealing in Dollar"),
  P("store", "sections", "المخزن (المنتجات)", "Store (Products)"),
  P("settings", "sections", "الإعدادات", "Settings"),
  P("edit_movements", "movements", "تعديل الحركات", "Edit Movements"),
  P("delete_movements", "movements", "حذف الحركات", "Delete Movements"),
  P("company_settings", "settings", "إعدادات الورقة الرسمية", "Official Paper Settings"),
  P("invested_capital", "dashboard_cards", "عرض رأس المال المستثمر في لوحة التحكم", "View Invested Capital on Dashboard"),
  P("view_main_vault_iqd", "dashboard_cards", "عرض الخزنة الرئيسية بالدينار في لوحة التحكم", "View Main IQD Vault on Dashboard"),
  P("view_main_vault_usd", "dashboard_cards", "عرض خزينة الدولار الرئيسية في الدولار", "View Main USD Vault in Dollar Section"),
  P("system_maintenance", "settings", "صيانة النظام", "System Maintenance"),
  // D-133: the codes card becomes the Security card; the token keeps its id and gets the card's new name.
  P("security_codes", "settings", "الأمان (رمز PIN، التحقق بخطوتين، المهلة، الأجهزة، ساعات العمل)", "Security card (PIN, 2FA, idle timeout, devices, working hours)"),
  P("show_customer_delete_edit", "customers", "الحذف والتعديل لحساب العميل", "Delete & Edit Customer Account"),
  P("delete_customers", "customers", "حذف حساب العميل", "Delete Customer Account"),
  P("edit_customers", "customers", "التعديل على اسم العميل في سجل العملاء", "Edit Customer Name"),
  P("audit_logs", "audit", "سجل التدقيق الحركات (المعدلة والمحذوفة )", "Movement Audit Log"),
  P("financials", "sections", "الأرباح والمصاريف", "Profits & Expenses"),
  P("match", "sections", "مطابقة الصناديق والورديات", "Box & Shift Matching"),
  P("transfers_cash", "sections", "الحوالات النقدية", "Cash Transfers"),
  P("sub_safes", "custody", "عرض الخزنات الفرعية", "View Sub-Safes"),
  P("digital_profits_management", "settings", "إدارة الأرباح الرقمية", "Digital Profits Management"),
  P("view_movements_by_employee", "movements", "عرض القيود حسب الموظف", "View Movements by Employee"),
  P("edit_user_details", "users", "عرض تعديل بيانات المستخدم", "View/Edit User Details"),
  P("view_debts_and_trusts", "customers", "عرض الديون والامانات", "View Debts & Trusts"),
  P("audit_control_section", "sections", "قسم الرقابة والتدقيق", "Audit & Control Section"),
  P("customer_portal", "sections", "المنصة الرقمية", "Digital Platform"),
  P("installments", "sections", "قسم الأقساط والبيع بالتقسيط", "Installments Management"),

  // --- D-129 extensions ---
  P("open_shift_for_other", "custody", "فتح وردية لمستخدم آخر", "Open a shift for another user", "D-129"),
  P("approve_cash_differences", "custody", "اعتماد فروقات الصندوق", "Approve cash differences", "D-129"),
  P("transfer_from_any_custody", "custody", "التحويل من أي عهدة", "Transfer from any custody", "D-129"),
  P("operate_another_custody", "custody", "العمل على عهدة مستخدم آخر", "Operate another user's custody", "D-129"),
  P("undo_movements", "movements", "التراجع عن حذف أو تعديل", "Undo a cancel or edit", "D-129"),
  P("view_logs_watchdog", "audit", "عرض السجلات ونظام الحراسة", "View logs / watchdog", "D-129"),
  P("manage_users", "users", "إدارة المستخدمين", "Manage users", "D-129"),
  P("manage_roles", "users", "إدارة الأدوار والصلاحيات", "Manage roles and permissions", "D-129"),
  // per-card settings tokens (D-129 "manage settings (per card)"); company_settings,
  // system_maintenance, security_codes, digital_profits_management above cover their cards.
  P("settings_notifications", "settings", "إعدادات الإشعارات والتنبيهات", "Notification settings card", "D-129"),
  P("settings_boxes", "settings", "إدارة الصناديق والخزنة", "Boxes & vault card", "D-129"),
  P("settings_sub_safes", "settings", "إدارة الخزنات الفرعية", "Sub-safes card", "D-129"),
  P("settings_partners", "settings", "إدارة الشركاء", "Partners card", "D-129"),
  P("settings_currencies", "settings", "خانة السوبر (العملات والأسعار)", "Super currencies & rates card", "D-129"),
  P("settings_storefront", "settings", "تنظيم واجهة المكتب", "Storefront card", "D-129"),

  // --- engine tokens (B1, D-214, D-215, D-211, D-118, D-119) ---
  P("manage_sub_safes", "custody", "تفعيل وإلغاء وإعادة توزيع الخزنات الفرعية", "Activate / deactivate / re-allocate sub-safes", "D-214"),
  P("settle_profit", "money", "تصفية الأرباح وتحويلها", "Settle / convert profit", "D-214"),
  P("opening_balance", "money", "قيود الأرصدة الافتتاحية", "Opening balances", "D-214"),
  P("archive_period", "money", "أرشفة فترة", "Archive a period", "D-214"),
  P("usd_direct_deposit_withdraw", "money", "إيداع وسحب مباشر بالدولار", "USD direct deposit / withdrawal", "D-211"),
  P("currency_direct_deposit_withdraw", "money", "إيداع وسحب مباشر بالعملات", "Currency direct deposit / withdrawal", "D-215"),
  P("currency_opening_balance", "money", "رصيد افتتاحي للعملات", "Currency opening balance", "D-215"),
  P("view_currency_profits", "money", "عرض كشف تحليل أرباح بيع العملات", "View currency sales profit ranking", "D-243"),
  // [C26] D-263 (a): capital add / withdraw (incl. D-076 full-balance withdrawal) move cash into or out of a drawer; the old
  // card asked for the Super code (App.tsx:8909-8990, 5574-5646); D-018 replaced the code with the personal PIN, so a token gates the action.
  P("manage_capital", "money", "إضافة وسحب رأس المال", "Add / withdraw capital", "D-263"),
  // [C33] D07 add-ons (D-109 (5), D-111 – D-114; engine tokens D-296): the section tokens `installments` / `store` open
  // the screens (view); these gate the writes on every path (screens, gateway, journal).
  P("installments_create", "money", "إنشاء عقد أقساط", "Create an installment contract", "D-109"),
  P("installments_collect", "money", "تسديد الأقساط", "Collect installments", "D-109"),
  P("installments_edit_schedule", "money", "تعديل جدول الأقساط", "Edit an installment schedule", "D-109"),
  P("installments_cancel", "money", "إلغاء عقد أو دفعة أقساط", "Cancel an installment contract or payment", "D-109"),
  P("store_operate", "money", "شراء وبيع وإرجاع المواد", "Store purchases, sales and invoice returns", "D-111"),
  P("store_manual_return", "money", "مرتجع يدوي بدون فاتورة", "Manual return without an invoice", "D-113"),
  P("manage_warehouses", "money", "إدارة المخازن والتحويل بينها", "Manage warehouses and transfers", "D-114"),
  P("store_writeoff", "money", "إتلاف مواد من المخزن", "Write off stock", "D-114"),
  P("create_customers", "customers", "إنشاء حساب عميل", "Create customer", "D-018"),
  P("approve_operations", "money", "اعتماد العمليات (الطرف الثاني)", "Approve operations (second person)", "D-035"),
  P("recovery_approve", "users", "نائب السوبر منجر (اعتماد الاستعادة والأجهزة)", "Deputy Super Manager (recovery / device approvals)", "D-209"),
  // [C34] D-135 "visibility by token for any role": one token per Audit & Control sub-tab (the movement audit keeps
  // `audit_logs`; the old customer audit was gated by role admin / Super, the purchase lists by nothing, R-A8).
  P("audit_customers_log", "audit", "سجل تدقيق إدارة العملاء", "Customer Audit Log", "D-135"),
  P("audit_purchases_list", "audit", "عرض قوائم الشراء للمخزن", "Store Purchase Lists", "D-135"),
  P("audit_security_log", "audit", "سجل الأمان والدخول", "Security & Sign-in Log", "D-135"),
  P("audit_settings_log", "audit", "سجل الإعدادات والصلاحيات", "Settings & Permissions Log", "D-135"),
  // [C37] D-151: downloading the office's full encrypted export needs this token + PIN; every download is audited.
  P("download_backup", "settings", "تنزيل النسخة الاحتياطية", "Download backup", "D-151"),
];

export const PERMISSION_TOKENS: ReadonlySet<string> = new Set(PERMISSIONS.map((p) => p.token));

export function isPermissionToken(token: string): boolean {
  return PERMISSION_TOKENS.has(token);
}

export function permissionLabel(token: string, lang: "ar" | "en"): string {
  const p = PERMISSIONS.find((x) => x.token === token);
  return p ? p[lang] : token;
}

/**
 * Ready-made roles (D-034). `super_manager` holds every token by definition
 * and is never stored as a list (D-129). The other four are *defaults* the
 * Super Manager may edit (D-034); the role rows are created from these at
 * provisioning and a role change resets the user to the role's current set
 * (D-130).
 */
export type BuiltinRole = "super_manager" | "branch_manager" | "accountant" | "cashier" | "auditor";

export const BUILTIN_ROLES: Readonly<Record<BuiltinRole, { ar: string; en: string }>> = {
  super_manager: { ar: "سوبر منجر", en: "Super Manager" },
  branch_manager: { ar: "مدير فرع", en: "Branch Manager" },
  accountant: { ar: "محاسب", en: "Accountant" },
  cashier: { ar: "أمين صندوق", en: "Cashier" },
  auditor: { ar: "مدقق", en: "Auditor" },
};

const SECTIONS_DAILY = ["dashboard", "movements", "debts", "wallets", "currencies", "dollar", "transfers_cash", "view_debts_and_trusts"];

export const ROLE_DEFAULT_PERMISSIONS: Readonly<Record<Exclude<BuiltinRole, "super_manager">, readonly string[]>> = {
  // "all operations in their branch + approvals" (D-034)
  branch_manager: [
    ...SECTIONS_DAILY,
    "store",
    "installments",
    // [C33] D-109 (5), D-296: "all operations in their branch" (D-034) includes the D07 writes
    "installments_create",
    "installments_collect",
    "installments_edit_schedule",
    "installments_cancel",
    "store_operate",
    "store_manual_return",
    "manage_warehouses",
    "store_writeoff",
    "financials",
    "match",
    "settings",
    "sub_safes",
    "edit_movements",
    "delete_movements",
    "undo_movements",
    "show_customer_delete_edit",
    "delete_customers",
    "edit_customers",
    "create_customers",
    "view_movements_by_employee",
    "view_main_vault_iqd",
    "view_main_vault_usd",
    "audit_logs",
    "audit_control_section",
    "audit_customers_log", // [C34] D-135 (old: role admin / Super)
    "audit_purchases_list", // [C34] D-135 (old: anyone in the section)
    "audit_security_log", // [C34] D-135, D-049: failed sign-ins are the branch manager's alerts too
    "view_logs_watchdog", // [C34] D-049: the branch manager receives the watchdog's alerts, so he may open its window
    "approve_operations",
    "approve_cash_differences",
    "open_shift_for_other",
    "transfer_from_any_custody",
    "operate_another_custody",
    "manage_sub_safes",
    "settings_boxes",
    "settings_sub_safes",
    "settings_currencies",
    "edit_user_details",
  ],
  // "reports, profits and expenses; no cash handling" (D-034)
  accountant: ["dashboard", "movements", "debts", "view_debts_and_trusts", "financials", "currencies", "view_movements_by_employee", "invested_capital", "view_main_vault_iqd", "view_main_vault_usd", "audit_logs", "audit_control_section", "settle_profit", "audit_purchases_list"],
  // "daily operations only" (D-034)
  // [C32] D-305: + `match` — a cashier counts their own drawer (D-120); approving differences stays with managers
  cashier: [...SECTIONS_DAILY, "create_customers", "sub_safes", "match"],
  // "read-only + Audit & Control" (D-034): section tokens open the screens; no mutating token.
  auditor: ["dashboard", "movements", "debts", "view_debts_and_trusts", "wallets", "currencies", "financials", "view_movements_by_employee", "audit_logs", "audit_control_section", "view_logs_watchdog", "audit_customers_log", "audit_purchases_list", "audit_security_log", "audit_settings_log"],
};

/**
 * Effective permission set of a user (D-129, D-130): the Super Manager role
 * has everything; otherwise role tokens ∪ per-user grants − per-user
 * revocations. Unknown tokens are ignored (they cannot be stored, see the
 * CHECK in `core.role_permission`, but an old export might carry one).
 */
export function effectivePermissions(args: {
  readonly role: string;
  readonly rolePermissions: Iterable<string>;
  readonly userGrants?: Iterable<string>;
  readonly userRevocations?: Iterable<string>;
}): ReadonlySet<string> {
  if (args.role === "super_manager") return PERMISSION_TOKENS;
  const set = new Set<string>();
  for (const t of args.rolePermissions) if (PERMISSION_TOKENS.has(t)) set.add(t);
  for (const t of args.userGrants ?? []) if (PERMISSION_TOKENS.has(t)) set.add(t);
  for (const t of args.userRevocations ?? []) set.delete(t);
  return set;
}
